"use client";

import Image from "next/image";

/**
 * Build Web IT mark used as the centerpiece of every shutter loader.
 * Mirrors the navbar logo composition:
 *   • a slowly rotating globe (`only-globe-white.svg`)
 *   • the static `bw.svg` letterform stacked on top
 *   • the "BUILD WEB IT" wordmark next to it
 *
 * Stability notes (re: previously visible wobble during the slide):
 * ─────────────────────────────────────────────────────────────────
 *   • All sizing is **non-responsive**. The shutter sits *above*
 *     the route boundary, so during a route change the body below
 *     can lose/gain its scrollbar mid-cycle (≈15px viewport-width
 *     delta). If the icon/gap/wordmark used `sm:` variants, that
 *     scrollbar flicker would visibly snap the logo at the 640px
 *     breakpoint. Pinning everything to a single size eliminates
 *     that snap entirely.
 *   • Both SVG layers use explicit `width`/`height` props instead
 *     of `fill`. That gives Next.js a fixed intrinsic size so the
 *     layered images can never disagree on dimensions for even one
 *     frame while the route is hydrating.
 *   • `whitespace-nowrap` + `leading-none` on the wordmark stop any
 *     line-height/wrap reflow from resizing the flex row mid-slide.
 *   • `shrink-0` on the icon block keeps the flex row from being
 *     able to squeeze the logo if the wordmark's text metrics
 *     fluctuate by a fraction of a pixel during font swap.
 *
 * Exported standalone so other transition surfaces can render the
 * same mark without duplicating JSX.
 */
export function ShutterMark() {
  return (
    <div className="flex shrink-0 items-center gap-4">
      <div className="relative h-20 w-20 shrink-0">
        <Image
          src="/assets/logo/only-globe-white.svg"
          alt="Build Web IT"
          width={80}
          height={80}
          priority
          className="absolute inset-0 h-full w-full select-none object-contain animate-spin-slow"
        />
        <Image
          src="/assets/logo/bw.svg"
          alt=""
          aria-hidden
          width={80}
          height={80}
          priority
          className="absolute inset-0 h-full w-full select-none object-contain"
        />
      </div>
      <p className="select-none whitespace-nowrap text-3xl font-semibold leading-none tracking-wide text-white">
        BUILD WEB IT
      </p>
    </div>
  );
}

/**
 * Global "shutter" loader used everywhere in the app:
 *
 *   • As the static fallback rendered by every route-level `loading.tsx`
 *     (Next.js Suspense). Default `phase="covered"` keeps the panel held
 *     in place so the logo is visible the entire time the route is
 *     streaming.
 *   • As an animated overlay driven by a state machine — from
 *     `ShutterController` for `runShutter()` calls (locale switch,
 *     `TransitionLink`, etc.):
 *       idle → closing → covered (perform action) → opening → idle.
 *
 * Motion is a unidirectional **downward wipe**:
 *   idle    → translateY(-100%)   (parked above the viewport)
 *   closing → translateY(0)       (slide DOWN from above to cover)
 *   covered → translateY(0)       (held)
 *   opening → translateY(100%)    (continue sliding DOWN, off the bottom)
 *
 * After `opening` the panel has to silently teleport back above the
 * viewport so the next cycle can slide down again. We do that by
 * suppressing the CSS transition on the `idle` (and `covered`) phases —
 * the snap from translateY(100%) → translateY(-100%) is instant and
 * invisible, so the user only ever perceives a downward sweep.
 */
export type ShutterPhase = "idle" | "closing" | "covered" | "opening";

interface ShutterProps {
  /**
   * Animation phase. Defaults to `"covered"` so a bare `<Shutter />` is
   * a valid, framework-friendly static loader (route Suspense fallback,
   * imperative full-screen wait, etc.).
   */
  phase?: ShutterPhase;
}

const TRANSITION = "transform 420ms cubic-bezier(0.65, 0, 0.35, 1)";

// Per-phase transform + transition. `idle` and `covered` use no
// transition: that's what makes the post-`opening` reset (back above
// the viewport, ready for the next cycle) instant and invisible.
//
// We use `translate3d` (not `translateY`) so the panel is promoted
// to a *stable* GPU compositing layer for the entire cycle. With a
// plain 2D `translateY`, browsers may re-rasterize the layer's
// contents per frame at slightly different sub-pixel offsets, which
// shows up as a tiny wobble on the rotating globe inside the logo.
// `translate3d(0, …, 0)` keeps the layer pinned to integer device
// pixels so the logo's silhouette stays perfectly steady mid-slide.
const PHASE_STYLE: Record<
  ShutterPhase,
  { transform: string; transition: string }
> = {
  idle: { transform: "translate3d(0, -100%, 0)", transition: "none" },
  closing: { transform: "translate3d(0, 0%, 0)", transition: TRANSITION },
  covered: { transform: "translate3d(0, 0%, 0)", transition: "none" },
  opening: { transform: "translate3d(0, 100%, 0)", transition: TRANSITION },
};

export default function Shutter({ phase = "covered" }: ShutterProps) {
  const isCovering = phase === "closing" || phase === "covered";
  const { transform, transition } = PHASE_STYLE[phase];

  return (
    <div
      // Off-screen most of the time; only meaningful (and announced) while
      // it is actively covering content.
      aria-hidden={!isCovering}
      role="presentation"
      data-phase={phase}
      style={{
        transform,
        transition,
        willChange: "transform",
        // Force a stable compositor layer so the rotating globe inside
        // doesn't get re-rasterized at varying sub-pixel offsets while
        // the panel is sliding.
        backfaceVisibility: "hidden",
      }}
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-background ${
        isCovering ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      <ShutterMark />
    </div>
  );
}
